import { useCallback } from 'react';
import { useSetAtom } from 'jotai';
import { snackbarQueueAtom, type SnackbarSeverity, type SnackbarAction } from 'atoms/snackbarAtom';

// 同時に保持/表示する通知の最大件数。超過分は古いものから捨てる。
const MAX_SNACKBARS = 3;

// クライアント専用のトースト識別子（描画 key と除去対象の特定にのみ使用）。
let nextSnackbarId = 0;

export type ShowOptions = {
    // dedup（直前と同一 message+severity を捨てる）と MAX_SNACKBARS の上限切り捨てを
    // 両方バイパスする。丸ごとコピーの終端通知（useCopyJobBlocking.ts）のように、
    // 「1件の push は必ず1件の表示になる」ことを呼び出し元が保証したい場合に使う
    // （issue #47。dedup/上限切り捨てはどちらも、通常のエラートースト等の連続呼び出しを
    // 想定した簡易的な間引きであり、内容が偶然一致・多重発生した個別事象を間引いてしまう）。
    guaranteed?: boolean;
};

// 返す関数は useCallback で安定参照にする（useEffect の .catch から呼んでも
// 依存配列を汚さず、再フェッチのループを起こさない）。set は useSetAtom により安定。
// show* のシグネチャは従来どおり message 必須で、action・options は省略可能な後方互換引数。
export const useSnackbar = () => {
    const setQueue = useSetAtom(snackbarQueueAtom);
    const show = useCallback(
        (message: string, severity: SnackbarSeverity, action?: SnackbarAction, options?: ShowOptions) =>
            setQueue((prev) => {
                if (!options?.guaranteed) {
                    // 直近と同一の message+severity は重複表示しない（連続 dedup）。
                    const last = prev[prev.length - 1];
                    if (last && last.message === message && last.severity === severity) {
                        return prev;
                    }
                }
                const next = [...prev, { id: nextSnackbarId++, message, severity, action }];
                if (options?.guaranteed) return next;
                // 最大件数を超えたら古いものから捨てる。
                return next.length > MAX_SNACKBARS ? next.slice(next.length - MAX_SNACKBARS) : next;
            }),
        [setQueue],
    );
    const showSuccess = useCallback(
        (message: string, action?: SnackbarAction, options?: ShowOptions) => show(message, 'success', action, options),
        [show],
    );
    const showError = useCallback(
        (message: string, action?: SnackbarAction, options?: ShowOptions) => show(message, 'error', action, options),
        [show],
    );
    const showWarning = useCallback(
        (message: string, action?: SnackbarAction, options?: ShowOptions) => show(message, 'warning', action, options),
        [show],
    );
    return { showSuccess, showError, showWarning };
};
